import {
  AssessmentScope,
  AssessmentScopeIntegration,
  GroupAssessmentScope,
  ItemAssessmentScope,
} from "@p0security/shared/types/assessment";
import { Flex, List } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import { capitalize, every, groupBy } from "lodash";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { AllIntegrationLogos } from "../../Integrations/IntegrationLogo.tsx";

export const scopeLabel: Record<
  AssessmentScope["integration"],
  Partial<Record<AssessmentScope["type"], string>>
> = {
  aws: {
    project: "account",
  },
  azure: {
    project: "subscription",
  },
  custom: {
    project: "system",
  },
  gcloud: {
    group: "folder",
    project: "project",
  },
  k8s: {
    project: "cluster",
  },
  okta: {
    project: "directory",
  },
  workspace: {
    project: "organization",
  },
  "azure-ad": {
    project: "tenant",
  },
  "entra-id": {
    project: "tenant",
  },
};

const isItemScopes = (
  scopes: AssessmentScope[]
): scopes is ItemAssessmentScope[] =>
  every(scopes, (s) => s.type === "project");

const isGroupScopes = (
  scopes: AssessmentScope[]
): scopes is GroupAssessmentScope[] => every(scopes, (s) => s.type === "group");

const renderScopes = (ids: string[]) =>
  ids.length > 5 ? (
    <GraphTooltip title={ids.join(", ")}>
      {`${ids.at(0)} and ${ids.length - 1} more`}
    </GraphTooltip>
  ) : (
    ids.join(", ")
  );

export const TargetsList: React.FC<{
  targets: AssessmentScope[];
  actionItem?: React.ReactElement;
}> = ({ targets, actionItem }) => {
  const getTargetLogo = useCallback(
    (integration: AssessmentScopeIntegration) =>
      AllIntegrationLogos[integration],
    []
  );

  const grouped = useMemo(
    () => Object.entries(groupBy(targets, "integration")),
    [targets]
  ) as [AssessmentScopeIntegration, AssessmentScope[]][];

  const renderTargetItem = useCallback(
    ([integration, items]: [AssessmentScopeIntegration, AssessmentScope[]]) => (
      <List.Item>
        <Flex
          align="baseline"
          className="w-full"
          gap="0.5em"
          justify="space-between"
          wrap
        >
          <List.Item.Meta
            avatar={getTargetLogo(integration)}
            title={pluralize(
              capitalize(
                scopeLabel[integration]?.[items[0].type] ?? items[0].type
              ),
              items.length
            )}
            description={
              isItemScopes(items)
                ? renderScopes(items.map((i) => i.id))
                : isGroupScopes(items)
                  ? renderScopes(items.map((i) => i.label ?? i.value))
                  : "(entire organization)"
            }
          />
          {actionItem && <div>{actionItem}</div>}
        </Flex>
      </List.Item>
    ),
    [actionItem, getTargetLogo]
  );

  return <List dataSource={grouped} renderItem={renderTargetItem} />;
};
